import { useRef, useState } from "react";
import { KeyRound, Upload, Loader2, CheckCircle2, ShieldCheck, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MANAGERS = [
  { name: "1Password", how: "Settings → Export → choose 1PUX or CSV" },
  { name: "Bitwarden", how: "Tools → Export vault → .json (unencrypted) or .csv" },
  { name: "LastPass", how: "Advanced Options → Export → saves a .csv" },
  { name: "Chrome / Edge", how: "Passwords → ⋮ → Export passwords (.csv)" },
  { name: "Apple Passwords / Safari", how: "File → Export → Export all passwords (.csv)" },
  { name: "Firefox", how: "Passwords → ⋯ → Export logins (.csv)" },
  { name: "Dashlane", how: "My account → Export data → CSV" },
  { name: "KeePass / KeePassXC", how: "Database → Export → CSV or KeePass XML" },
  { name: "Passbolt", how: "Browser extension → Export → CSV (KDBX, LastPass or 1Password layout)" },
  { name: "Proton Pass / NordPass", how: "Settings → Export → CSV" },
];

const Preview = ({ preview, onImport, busy }) => (
  <div data-testid="import-preview" className="space-y-3">
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm flex items-center gap-2">
      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      <span>
        Detected <span className="font-medium" data-testid="import-format">{preview.format}</span> · <span data-testid="import-count">{preview.count}</span> logins
        {preview.duplicates > 0 && <span className="text-muted-foreground"> · {preview.duplicates} already in your vault (will be skipped)</span>}
      </span>
    </div>
    <div className="rounded-xl border border-border divide-y divide-border text-xs max-h-56 overflow-y-auto">
      {preview.sample.map((r, i) => (
        <div key={`${r.title}-${r.username}-${i}`} className="grid grid-cols-[1fr_1fr_auto] gap-2 px-3 py-2">
          <div className="min-w-0"><div className="font-medium truncate">{r.title}</div><div className="text-muted-foreground truncate">{r.url}</div></div>
          <div className="truncate text-muted-foreground self-center">{r.username}</div>
          <div className="font-mono self-center">{r.password}</div>
        </div>
      ))}
      {preview.count > preview.sample.length && <div className="px-3 py-2 text-muted-foreground">…and {preview.count - preview.sample.length} more</div>}
    </div>
    <p className="text-[11px] text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Each login becomes an encrypted Passwords card. The export file is discarded after import.</p>
    <Button data-testid="import-confirm-btn" onClick={onImport} disabled={busy} className="w-full gap-2 bg-primary hover:bg-primary/90 rounded-full">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Import {preview.count - preview.duplicates} logins
    </Button>
  </div>
);

export const PasswordImportModal = ({ open, onClose, onImported }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  const pick = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/passwords/import/preview`, fd);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read this export");
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("skip_duplicates", "true");
      const { data } = await axios.post(`${API}/passwords/import`, fd);
      setResult(data);
      toast.success(`${data.created} logins filed as encrypted passwords`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent data-testid="password-import-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> Import from a password manager</DialogTitle>
          <DialogDescription>Export from any manager, drop the file here, and every login is filed as an encrypted card — no retyping.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div data-testid="import-result" className="space-y-3">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" />
              <div className="mt-2 font-serif text-2xl" data-testid="import-created">{result.created} imported</div>
              <div className="text-xs text-muted-foreground mt-1">
                {result.skipped_duplicates > 0 && `${result.skipped_duplicates} duplicates skipped`}
                {result.skipped_plan_limit > 0 && ` · ${result.skipped_plan_limit} held back by the free-plan limit`}
              </div>
            </div>
            <Button data-testid="import-done-btn" onClick={() => { reset(); onClose(); }} className="w-full rounded-full">Done</Button>
          </div>
        ) : preview ? (
          <Preview preview={preview} onImport={run} busy={busy} />
        ) : (
          <div className="space-y-4">
            <button
              data-testid="import-dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
              className="w-full rounded-2xl border-2 border-dashed border-border hover:border-primary p-6 text-center transition-colors"
            >
              {busy ? <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" /> : <Upload className="h-6 w-6 text-primary mx-auto" />}
              <div className="mt-2 text-sm font-medium">Drop your export here or click to choose</div>
              <div className="text-xs text-muted-foreground mt-0.5">.csv · .json (Bitwarden) · .1pux (1Password) · .xml (KeePass)</div>
            </button>
            <input ref={fileRef} data-testid="import-file-input" type="file" accept=".csv,.json,.1pux,.xml,.zip,text/csv,application/json" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">How to export</div>
              <div className="rounded-xl border border-border divide-y divide-border text-xs max-h-44 overflow-y-auto" data-testid="export-instructions">
                {MANAGERS.map((m) => (
                  <div key={m.name} className="flex items-center gap-2 px-3 py-1.5"><span className="font-medium w-40 shrink-0">{m.name}</span><ChevronRight className="h-3 w-3 text-muted-foreground" /><span className="text-muted-foreground">{m.how}</span></div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
